import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { auth } from "../firebase/firebase";
import { RootState } from "../store/rootReducers";
import { selectUser } from "../store/slices/session/sessionSelector";
import { setUser } from "../store/slices/session/sessionSlice";
import * as Sentry from "@sentry/react";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BACKEND_URL + "/api",
  prepareHeaders: (headers, { getState }) => {
    const user = selectUser(getState() as RootState);
    if (user?.token) {
      headers.set("Authorization", `Bearer ${user.token}`);
    }
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

// Track token refresh attempts to prevent infinite loops
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Function to refresh token and return a promise
const refreshToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No user is logged in");
  }
  // Force refresh the token
  return user.getIdToken(true);
};

/**
 * This function handles reactive token refresh when API calls fail due to auth errors.
 * It works alongside the proactive refresh in useTokenRefresh hook.
 */

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // First attempt with current token
  let result = await baseQuery(args, api, extraOptions);

  // Handle unauthorized or forbidden responses (token expired)
  if (
    result.error &&
    (result.error.status === 401 || result.error.status === 403)
  ) {
    // If we're not already refreshing the token
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        // Create a single refresh promise that all concurrent requests can use
        refreshPromise = refreshToken();
        const newToken = await refreshPromise;

        // Get current user to update store
        const user = auth.currentUser;
        if (user) {
          // Update the token in Redux store
          api.dispatch(
            setUser({
              uid: user.uid,
              email: user.email || "",
              token: newToken,
              creationTime: user.metadata.creationTime || "",
              lastSignInTime: user.metadata.lastSignInTime || "",
            })
          );

          // Retry the initial query with new token
          result = await baseQuery(args, api, extraOptions);
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error refreshing token:", error);
        // If token refresh fails, log the user out
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    } else if (refreshPromise) {
      // If another request is already refreshing, wait for that to complete
      try {
        await refreshPromise;
        // Retry with the new token that should now be in the store
        result = await baseQuery(args, api, extraOptions);
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
        // The refresh failed, but we don't need to do anything as the first request already handled it
      }
    }
  }

  return result;
};
